import React, { useEffect } from 'react';
import { Button, Form, Input, Select, Space, Tag, Typography } from 'antd';
import Drawer from '../../../components/AppDrawer';
import { MailOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getCustomerSegmentsForSelect, getRolesForSelect, getUser } from '../api/users';
import AuditProperties from '../../../components/AuditProperties';
import { useResendInvite } from '../hooks/useResendInvite';
import { accountStatusLabel, accountStatusTagColor, isPendingAccountStatus } from '../utils/accountStatus';
import Can from '../../permissions/Can';

const { Text } = Typography;

interface UserEditDrawerProps {
  open: boolean;
  userId: string | null;
  onClose: () => void;
  onSave: (values: {
    firstName: string;
    lastName: string;
    email: string;
    roleIds: string[];
    customerSegmentIds: string[];
  }) => void;
  saving: boolean;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  roleIds: string[];
  customerSegmentIds: string[];
}

const UserEditDrawer: React.FC<UserEditDrawerProps> = ({
  open,
  userId,
  onClose,
  onSave,
  saving,
}) => {
  const [form] = Form.useForm<FormValues>();
  const resendInviteMutation = useResendInvite();

  const { data: roleOptions = [] } = useQuery({
    queryKey: ['roles-select'],
    queryFn: getRolesForSelect,
    enabled: open,
  });

  const { data: segmentOptions = [] } = useQuery({
    queryKey: ['customer-segments-select'],
    queryFn: getCustomerSegmentsForSelect,
    enabled: open,
  });

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUser(userId!),
    enabled: open && userId !== null,
  });

  const isPending = user ? isPendingAccountStatus(user.accountStatus) : false;

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        roleIds: user.roleIds ?? [],
        customerSegmentIds: user.customerSegmentIds ?? [],
      });
    }
  }, [open, user, form]);

  const handleResendInvite = () => {
    if (!userId) {
      return;
    }
    resendInviteMutation.mutate(userId);
  };

  return (
    <Drawer
      title="Edit user"
      size="large"
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            {isPending && userId && (
              <Can permission="users.edit">
                <Button
                  icon={<MailOutlined />}
                  loading={resendInviteMutation.isPending}
                  onClick={handleResendInvite}
                >
                  Resend invitation
                </Button>
              </Can>
            )}
          </div>
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            Save
          </Button>
        </Space>
      }
    >
      {isLoading || !user ? null : (
        <>
          <Space style={{ marginBottom: 16 }} align="center">
            <Text type="secondary">Account status</Text>
            <Tag color={accountStatusTagColor(user.accountStatus)}>
              {accountStatusLabel(user.accountStatus)}
            </Tag>
          </Space>
          {isPending && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              This user has not set a password yet. Send a new invitation email with a fresh setup link
              (valid for 72 hours).
            </Text>
          )}
          <div className="cp-form-section">
            <Form form={form} layout="vertical" onFinish={onSave}>
              <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email' }]}>
                <Input placeholder="user@example.com" />
              </Form.Item>
              <Form.Item name="firstName" label="First name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="lastName" label="Last name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="roleIds" label="Roles">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select roles"
                  options={roleOptions.map((r) => ({ value: r.id, label: r.name }))}
                />
              </Form.Item>
              <Form.Item name="customerSegmentIds" label="Customer segments">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="None = org-wide visibility"
                  options={segmentOptions.map((s) => ({ value: s.id, label: s.title }))}
                />
              </Form.Item>
            </Form>
          </div>
        </>
      )}
      {user && (
        <AuditProperties
          className="cp-form-section"
          style={{ marginTop: 16 }}
          values={{
            createdAtUtc: user.createdAtUtc,
            createdByDisplayName: user.createdByDisplayName,
            createdByUserId: user.createdByUserId,
            lastModifiedAtUtc: user.lastModifiedAtUtc,
            modifiedByDisplayName: user.modifiedByDisplayName,
            modifiedByUserId: user.modifiedByUserId,
          }}
        />
      )}
    </Drawer>
  );
};

export default UserEditDrawer;
